import React, { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Hide, Show } from "@/assets/icons/icons";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import type { FormFieldProps } from "@/components/Interface/FormFields/FormField/FormField.utils";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

/** What the toggle offers next: hide while the password shows, show otherwise. */
const visibilityMode = {
  shown: {
    inputType: "text",
    labelKey: "translation:label.hidePassword",
    Icon: Hide,
    iconTestid: "hide-password-icon",
  },
  hidden: {
    inputType: "password",
    labelKey: "translation:label.showPassword",
    Icon: Show,
    iconTestid: "show-password-icon",
  },
} as const;

/**
 * Text input that toggles between password and plain-text mode via a
 * Show/Hide button in the right icon slot. Wraps FormField so it inherits
 * all layout, validation, and styling behaviour from the shared form system.
 */
export const PasswordField = <TFormValues extends FieldValues>(
  props: React.PropsWithChildren<FormFieldProps<TFormValues>>,
) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const isDisabled = !!props.options?.disabled;
  const { inputType, labelKey, Icon, iconTestid } =
    visibilityMode[showPassword ? "shown" : "hidden"];

  const icon = (
    <button
      className="group relative flex shrink-0 items-center rounded-md outline-none"
      type="button"
      tabIndex={isDisabled ? -1 : KEYBOARD_TAB_INDEX}
      onClick={() => setShowPassword((v) => !v)}
      aria-label={t(labelKey)}
      disabled={isDisabled}
    >
      <FocusRing />
      <Icon
        data-testid={iconTestid}
        className="text-foreground-primary"
        width={24}
        height={24}
      />
    </button>
  );

  return <FormField {...props} type={inputType} icon={icon} />;
};
